import { GameModeConfig, ChallengeScenario, EducationalModule } from '../types/gameModes';

export const gameModes: GameModeConfig[] = [
  {
    id: 'sandbox',
    name: 'Sandbox Mode',
    description: 'Experience the full life cycle with complete freedom. Set your own goals and experiment with different life paths.',
    icon: '🎮',
    color: '#3b82f6',
    gradient: 'from-blue-500 to-blue-600',
    objectives: [
      'Explore different life paths',
      'Set your own goals',
      'Experience consequences of choices',
      'Learn through experimentation'
    ],
    specialRules: [
      'Complete freedom in decision making',
      'No specific objectives to complete',
      'Focus on exploration and learning'
    ],
    scoringSystem: 'default'
  },
  {
    id: 'challenge',
    name: 'Challenge Scenarios',
    description: 'Take on specific challenges with defined goals and starting conditions. Test your strategic thinking!',
    icon: '🎯',
    color: '#ef4444',
    gradient: 'from-red-500 to-red-600',
    objectives: [
      'Complete specific challenges',
      'Achieve defined goals',
      'Overcome difficult situations',
      'Master strategic planning'
    ],
    specialRules: [
      'Predefined starting conditions',
      'Specific success criteria',
      'Time-limited challenges',
      'Strategic decision making required'
    ],
    scoringSystem: 'custom'
  },
  {
    id: 'educational',
    name: 'Educational Mode',
    description: 'Learn through guided narrative with lessons, questions, and reflection points. Perfect for workshops!',
    icon: '📚',
    color: '#10b981',
    gradient: 'from-emerald-500 to-emerald-600',
    objectives: [
      'Learn life planning concepts',
      'Understand decision consequences',
      'Reflect on choices made',
      'Develop strategic thinking'
    ],
    specialRules: [
      'Guided narrative experience',
      'Educational content and questions',
      'Reflection points throughout',
      'Focus on learning outcomes'
    ],
    scoringSystem: 'default'
  }
];

export const challengeScenarios: ChallengeScenario[] = [
  {
    id: 'rich-retirement',
    name: 'Rich Retirement',
    description: 'Start as a young person and aim to retire at 65 with substantial savings and investments for a high standard of living.',
    icon: '💰',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-amber-600',
    objectives: [
      'Retire at age 65 with $2,000,000+ in savings',
      'Maintain wealth score above 80',
      'Have diversified investments',
      'Avoid major debt throughout life'
    ],
    startingConditions: {
      wealth: 10,
      education: 5,
      career: 5
    },
    specialRules: [
      'Focus on career advancement and investments',
      'Minimize unnecessary expenses',
      'Plan for long-term financial security',
      'Balance risk and reward in investments'
    ],
    successConditions: {
      age: 65,
      stats: { wealth: 80 },
      money: 2000000
    },
    failureConditions: {
      debt: 100000,
      health: 20
    },
    tips: [
      'Start investing early - compound interest is powerful',
      'Choose high-paying career paths',
      'Avoid lifestyle inflation',
      'Diversify your investments',
      'Consider real estate investments'
    ],
    difficulty: 'hard'
  },
  {
    id: 'uphill-battle',
    name: 'Uphill Battle',
    description: 'Face a series of unexpected difficulties and challenges. Survive to retirement with positive outcomes despite the odds.',
    icon: '⛰️',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-violet-600',
    objectives: [
      'Survive major life crises',
      'Reach retirement without major debt',
      'Maintain basic financial security',
      'Overcome at least 3 major setbacks'
    ],
    startingConditions: {
      wealth: 5,
      health: 70,
      happiness: 60
    },
    specialRules: [
      'Face random crisis events more frequently',
      'Limited financial resources',
      'Health challenges throughout life',
      'Economic downturns and job losses'
    ],
    successConditions: {
      age: 65,
      stats: { wealth: 40 },
      debt: 0
    },
    failureConditions: {
      debt: 200000,
      health: 0
    },
    tips: [
      'Build emergency savings early',
      'Maintain good health habits',
      'Develop multiple income streams',
      'Stay adaptable to change',
      'Focus on essential expenses only'
    ],
    difficulty: 'hard'
  },
  {
    id: 'life-balance',
    name: 'Life Balance',
    description: 'Maintain high levels of happiness and life satisfaction throughout your life. Balance work, health, relationships, and personal growth.',
    icon: '⚖️',
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-cyan-600',
    objectives: [
      'Maintain happiness above 80 throughout life',
      'Keep all major stats above 60',
      'Achieve work-life balance',
      'Build strong relationships'
    ],
    startingConditions: {
      happiness: 80,
      health: 80,
      relationships: 80
    },
    specialRules: [
      'Focus on balanced development',
      'Avoid neglecting any life area',
      'Prioritize well-being over wealth',
      'Build meaningful connections'
    ],
    successConditions: {
      age: 65,
      stats: { 
        happiness: 80, 
        health: 60, 
        relationships: 60,
        career: 60,
        wealth: 60
      }
    },
    failureConditions: {
      health: 30
    },
    tips: [
      'Don\'t sacrifice health for career',
      'Invest time in relationships',
      'Pursue hobbies and passions',
      'Take regular breaks and vacations',
      'Practice stress management'
    ],
    difficulty: 'medium'
  },
  {
    id: 'entrepreneur-journey',
    name: 'Entrepreneur Journey',
    description: 'Build and grow your own business empire. Start small and scale up to create a successful enterprise.',
    icon: '🚀',
    color: '#f97316',
    gradient: 'from-orange-500 to-orange-600',
    objectives: [
      'Start at least 2 successful businesses',
      'Achieve wealth score above 90',
      'Create multiple income streams',
      'Build a business legacy'
    ],
    startingConditions: {
      wealth: 15,
      career: 10,
      education: 20
    },
    specialRules: [
      'Focus on business opportunities',
      'Take calculated risks',
      'Build networks and partnerships',
      'Innovate and adapt to market changes'
    ],
    successConditions: {
      age: 60,
      stats: { wealth: 90, career: 85 },
      money: 5000000
    },
    failureConditions: {
      debt: 500000
    },
    tips: [
      'Start with small, manageable businesses',
      'Reinvest profits for growth',
      'Build strong business relationships',
      'Stay ahead of market trends',
      'Diversify your business portfolio'
    ],
    difficulty: 'hard'
  },
  {
    id: 'family-first',
    name: 'Family First',
    description: 'Prioritize family and relationships above all else. Build a loving, supportive family environment.',
    icon: '👨‍👩‍👧‍👦',
    color: '#ec4899',
    gradient: 'from-pink-500 to-pink-600',
    objectives: [
      'Get married and have children',
      'Maintain relationships above 85',
      'Provide for family needs',
      'Create lasting family memories'
    ],
    startingConditions: {
      relationships: 85,
      happiness: 75
    },
    specialRules: [
      'Prioritize family decisions',
      'Focus on relationship building',
      'Balance work and family time',
      'Make family-friendly choices'
    ],
    successConditions: {
      age: 65,
      stats: { 
        relationships: 85, 
        happiness: 80,
        wealth: 50
      }
    },
    failureConditions: {
      health: 30
    },
    tips: [
      'Spend quality time with family',
      'Communicate openly and honestly',
      'Support family members\' goals',
      'Create family traditions',
      'Balance work and family commitments'
    ],
    difficulty: 'medium'
  }
];

export const educationalModules: EducationalModule[] = [
  {
    id: 'financial-literacy',
    name: 'Financial Literacy',
    description: 'Learn essential financial concepts through interactive life decisions and real-world scenarios.',
    icon: '💳',
    color: '#059669',
    gradient: 'from-emerald-500 to-emerald-600',
    lessons: [
      {
        id: 'budgeting-basics',
        title: 'Budgeting and Money Management',
        content: 'Understanding income, expenses, and how to create a sustainable budget for different life stages.',
        questions: [
          {
            id: 'budget-priority',
            question: 'What should be your first priority when creating a budget?',
            options: [
              'Entertainment and leisure',
              'Essential expenses (housing, food, utilities)',
              'Luxury items and vacations',
              'Investments and savings'
            ],
            correctAnswer: 1,
            explanation: 'Essential expenses should always come first in any budget. These are the necessities for survival and basic well-being.',
            relatedDecision: 'decision-6'
          }
        ],
        reflectionPoints: [
          'How do your spending choices affect your long-term financial security?',
          'What expenses could you reduce to increase your savings?',
          'How might your budget change as you age and your circumstances change?'
        ],
        ageTrigger: 18
      },
      {
        id: 'investment-fundamentals',
        title: 'Investment Fundamentals',
        content: 'Learn about different investment options, risk management, and building wealth over time.',
        questions: [
          {
            id: 'investment-risk',
            question: 'What is the relationship between risk and potential returns in investments?',
            options: [
              'Higher risk always means higher returns',
              'Lower risk investments typically offer higher returns',
              'Higher risk investments typically offer higher potential returns',
              'Risk and returns are completely unrelated'
            ],
            correctAnswer: 2,
            explanation: 'Generally, higher risk investments offer higher potential returns, but also higher potential losses. It\'s important to balance risk with your financial goals and timeline.',
            relatedDecision: 'decision-13'
          }
        ],
        reflectionPoints: [
          'How comfortable are you with investment risk?',
          'What are your long-term financial goals?',
          'How might diversification help protect your investments?'
        ],
        ageTrigger: 25
      }
    ],
    targetAge: 18,
    focus: ['money management', 'investments', 'financial planning', 'debt management']
  },
  {
    id: 'career-development',
    name: 'Career Development',
    description: 'Explore career planning, skill development, and professional growth strategies.',
    icon: '💼',
    color: '#7c3aed',
    gradient: 'from-violet-500 to-violet-600',
    lessons: [
      {
        id: 'career-planning',
        title: 'Strategic Career Planning',
        content: 'Learn how to plan your career path, develop skills, and make strategic professional decisions.',
        questions: [
          {
            id: 'career-advancement',
            question: 'What is the most important factor for long-term career success?',
            options: [
              'Having the highest salary possible',
              'Continuous learning and skill development',
              'Working the most hours',
              'Having the most prestigious job title'
            ],
            correctAnswer: 1,
            explanation: 'Continuous learning and skill development are crucial for long-term career success. The job market evolves rapidly, and those who adapt and grow their skills stay relevant and valuable.',
            relatedDecision: 'decision-4'
          }
        ],
        reflectionPoints: [
          'What skills do you need to develop for your desired career path?',
          'How can you balance career advancement with personal life?',
          'What role does networking play in career development?'
        ],
        ageTrigger: 22
      }
    ],
    targetAge: 22,
    focus: ['career planning', 'skill development', 'professional growth', 'work-life balance']
  },
  {
    id: 'life-balance',
    name: 'Life Balance & Well-being',
    description: 'Understand the importance of balancing different life areas for overall happiness and success.',
    icon: '🧘',
    color: '#0891b2',
    gradient: 'from-cyan-500 to-cyan-600',
    lessons: [
      {
        id: 'work-life-balance',
        title: 'Work-Life Balance',
        content: 'Learn strategies for maintaining healthy relationships between work, personal life, health, and relationships.',
        questions: [
          {
            id: 'balance-priority',
            question: 'What happens when you neglect your health for career advancement?',
            options: [
              'Nothing - health doesn\'t affect career success',
              'Short-term gains but long-term health problems',
              'Immediate career benefits with no consequences',
              'Health automatically improves with career success'
            ],
            correctAnswer: 1,
            explanation: 'Neglecting health for career can lead to short-term gains but often results in long-term health problems, reduced productivity, and decreased quality of life.',
            relatedDecision: 'decision-3'
          }
        ],
        reflectionPoints: [
          'How do you currently balance work and personal life?',
          'What activities help you maintain good mental and physical health?',
          'How can you better prioritize your well-being?'
        ],
        ageTrigger: 25
      }
    ],
    targetAge: 25,
    focus: ['work-life balance', 'health management', 'relationship building', 'stress management']
  }
];
